import { getProfileDataThunk } from '../../../store/workspace/thunks';
import { thousandSeparator } from '../../../utils/numberHelpers';
import { useDispatch, useSelector } from '../../../store/store';
import { MemberDetail } from '../../../store/workspace/types';
import MotionWrapper from '../../../layouts/MotionWrapper';
import AccountSkeleton from './components/Skeleton';
import Button from '../../../components/Button';
import { useNavigate } from 'react-router-dom';
import { plansEnum } from '../../../configs';
import { useEffect, useState } from 'react';
import styles from './style.module.scss';
import {
    selectProfile,
    selectLogoImage,
    selectFetchingProfile,
} from '../../../store/workspace/selectors';
import {
    PlusIcon,
    LeaveIcon,
    PencilIcon,
    LockAltIcon,
    LoadingIcon,
    UserTickIcon,
    ChevronDownIcon,
    InformationIcon,
    CheckCircleIcon,
} from '../../../assets/icons';
import {
    setPlansModalShow,
    setEditEmailModal,
    setInviteMemberModal,
    setChangePasswordModal,
    setAddExtraMemberModal,
    setEditPhoneNumberModal,
    setEditAccountInfoModal,
    setEditCompanyInfoModal,
    setSelectedMemberDetail,
    setAddExtraJobPostModal,
    setAddExtraApplicantModal,
    setConfirmRemoveMemberModal,
    setConfirmLeaveOrganizationModal,
} from '../../../store/workspace/slice';

type PlansTab = 'Current' | 'History';

export default function Account() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const logo = useSelector(selectLogoImage);
    const profile = useSelector(selectProfile);
    const fetchingProfile = useSelector(selectFetchingProfile);
    const [openedPlans, setOpenedPlans] = useState<number[]>([]);
    const [plansTab, setPlansTab] = useState<PlansTab>('Current');
    const plansList = (plansTab === 'Current') ? profile?.userPlan?.slice(-1) : profile?.userPlan?.slice(0, profile?.userPlan?.length - 1);

    useEffect(() => {
        dispatch(getProfileDataThunk());
    }, []);

    const plansDetailManager = (indx: number) => {
        const check = openedPlans.includes(indx);
        if (check) {
            setOpenedPlans(prev => prev.filter(i => i !== indx));
        } else {
            setOpenedPlans(prev => [...prev, indx]);
        }
    };

    const changePlansTab = (tab: PlansTab) => {
        setOpenedPlans([]);
        setPlansTab(tab);
    };

    const inviteMember = () => {
        if (!profile?.invitations || !profile.memberCounter) return;

        if (profile.planType === 'free_trial') {
            dispatch(setPlansModalShow(true));
            return;
        }

        if (profile.invitations &&
            profile.invitations.length < profile.memberCounter) {
            dispatch(setInviteMemberModal(true));
            return
        }

        if (profile.invitations &&
            profile.invitations.length === profile.memberCounter) {
            dispatch(setAddExtraMemberModal(true))
            return
        }
    };

    const removeMember = (member: MemberDetail) => {
        dispatch(setSelectedMemberDetail(member));
        dispatch(setConfirmRemoveMemberModal(true));
    };

    const navigateToPayment = () => {
        navigate('/workspace/payment');
        dispatch(setPlansModalShow(true));
    };

    const addMoreMember = () => {
        if (!profile?.planType) return;

        if (profile.planType === 'free_trial') {
            dispatch(setPlansModalShow(true));
            return;
        }

        dispatch(setAddExtraMemberModal(true));
    };

    const addMoreJobPost = () => {
        if (!profile?.planType) return;

        if (profile.planType === 'free_trial') {
            dispatch(setPlansModalShow(true));
            return;
        }

        dispatch(setAddExtraJobPostModal(true));
    };

    const addMoreApplicant = () => {
        if (!profile?.planType) return;

        if (profile.planType === 'free_trial') {
            dispatch(setPlansModalShow(true));
            return;
        }

        dispatch(setAddExtraApplicantModal(true));
    };

    return (
        <MotionWrapper className={styles.container}>
            {fetchingProfile ? <AccountSkeleton /> : (
                <>
                    <div className={styles.account}>
                        <span className={styles.account_title}>Account Information</span>
                        <div className={styles.basic_info}>
                            {profile?.userRole === 'member' && <div className={styles.basic_info_member}>
                                <span>Member account</span>
                                <div
                                    className={styles.basic_info_member_action}
                                    onClick={() => dispatch(setConfirmLeaveOrganizationModal(true))}
                                >
                                    <span>Leave organization</span>
                                    <LeaveIcon />
                                </div>
                            </div>}
                            <div className={styles.basic_info_header}>
                                <span>Title: </span>
                                {profile?.userRole === 'owner' && <PencilIcon onClick={() => dispatch(setEditAccountInfoModal(true))} />}
                            </div>
                            <b>{profile?.employerRole?.title}</b>
                            <div className={styles.basic_info_content}>
                                <div className={styles.basic_info_content_item}>
                                    <span className={styles.property}>First name: <b>{profile?.firstName}</b></span>
                                </div>
                                <div className={styles.basic_info_content_item}>
                                    <span className={styles.property}>Last name: <b>{profile?.lastName}</b></span>
                                </div>
                                <div className={styles.basic_info_content_item}>
                                    <span className={styles.property}>Phone number: <b>{profile?.phoneNumber || '----'}</b></span>
                                    {profile?.userRole === 'owner' && <PencilIcon onClick={() => dispatch(setEditPhoneNumberModal(true))} />}
                                </div>
                                <div className={styles.basic_info_content_item}>
                                    <span className={styles.property}>Email: <b>{profile?.email || '----'}</b></span>
                                    {profile?.userRole === 'owner' && <PencilIcon onClick={() => dispatch(setEditEmailModal(true))} />}
                                </div>
                            </div>
                            <div
                                className={styles.basic_info_change_password}
                                onClick={() => dispatch(setChangePasswordModal(true))}
                            >
                                <LockAltIcon />
                                <span>Change Login Password</span>
                            </div>
                        </div>
                        <div className={styles.company_info}>
                            <div className={styles.company_info_header}>
                                <div className={styles.company_info_header_title}>
                                    <span>Company name:</span>
                                    <div className={styles.group}>
                                        <div className={styles.group_logo}>
                                            {logo ? <img
                                                src={logo}
                                                alt=''
                                            /> : <span>{profile?.companyName?.slice(0, 1)}</span>}
                                        </div>
                                        <b className={styles.group_name}>{profile?.companyName}</b>
                                    </div>
                                </div>
                                {profile?.userRole === 'owner' && <PencilIcon onClick={() => dispatch(setEditCompanyInfoModal(true))} />}
                            </div>
                            <div className={styles.company_info_detail}>
                                <div className={styles.company_info_detail_item}>
                                    <span className={styles.property}>Company website: <b>{profile?.companyWebsite}</b></span>
                                </div>
                                <div className={styles.company_info_detail_item}>
                                    <span className={styles.property}>Company size: <b>{profile?.companySize?.title}</b></span>
                                </div>
                            </div>
                            <div className={styles.company_info_extra}>
                                <div className={styles.company_info_extra_address}>
                                    <span className={styles.property}>Address: <b>{profile?.address}</b></span>
                                </div>
                                <div className={styles.company_info_extra_location}>
                                    <span className={styles.property}>City: <b>{profile?.city}</b></span>
                                    <span className={styles.property}>State: <b>{profile?.state}</b></span>
                                    <span className={styles.property}>Country: <b>{profile?.country}</b></span>
                                    <span className={styles.property}>Zip code: <b>{profile?.zipCode}</b></span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {profile?.userRole === 'owner' && <div className={styles.seats}>
                        <div className={styles.seats_header}>
                            <span className={styles.seats_header_title}>
                                Account Seats ({profile.invitations.length}/{profile.memberCounter})
                            </span>
                            <span
                                className={styles.seats_header_add}
                                onClick={addMoreMember}
                            >
                                Add More
                            </span>
                        </div>
                        {profile?.invitations?.map(item => <div className={styles.seats_member}>
                            <div className={styles.seats_member_status}>
                                {item.status === 'pending' && <LoadingIcon />}
                                {item.status === 'accepted' && <UserTickIcon />}
                                <span>Member seat: {item.status}</span>
                            </div>
                            <div className={styles.seats_member_detail}>
                                <span className={styles.seats_member_detail_email}>{item.email}</span>
                                <span
                                    className={styles.seats_member_detail_remove}
                                    onClick={() => removeMember(item)}
                                >
                                    Remove
                                </span>
                            </div>
                        </div>)}
                        <div
                            onClick={inviteMember}
                            className={styles.seats_add}
                        >
                            <PlusIcon />
                            <span>Add member seat</span>
                        </div>
                    </div>}

                    <div className={styles.plan_info}>
                        <span className={styles.plan_info_title}>
                            Plan Information
                        </span>
                        <div className={styles.plan_info_tab}>
                            <div
                                className={styles.plan_info_tab_item}
                                onClick={() => changePlansTab('Current')}
                                data-is-active={plansTab === 'Current'}
                            >
                                <span>Current plan</span>
                            </div>
                            <div
                                className={styles.plan_info_tab_item}
                                onClick={() => changePlansTab('History')}
                                data-is-active={plansTab === 'History'}
                            >
                                <span>Plan’s history</span>
                            </div>
                        </div>

                        <div className={styles.plan_info_list}>
                            {plansList && plansList.length > 0 ? <>
                                {plansList.map((item, indx) => {
                                    const isDetailShow = openedPlans.includes(indx);

                                    return (<div className={styles.plan}>
                                        <span className={styles.plan_name}>
                                            {item.level ? `${plansEnum[item.level]} plan` : ''}
                                            <b>{` (Started at ${item.modify?.slice(0, 10)})`}</b>
                                        </span>
                                        <div className={styles.plan_detail}>
                                            <div className={styles.plan_detail_features}>
                                                <div className={styles.plan_detail_features_item}>
                                                    <CheckCircleIcon />
                                                    <span><b>{item.jobList?.length}/{item.planCounter}</b> Job postings per year</span>
                                                    {plansTab === 'Current' && <h4 onClick={addMoreJobPost}>Add More</h4>}
                                                </div>
                                                <div className={styles.plan_detail_features_item}>
                                                    <CheckCircleIcon />
                                                    <span><b>{item.applicantCount}/{item.examinerCount}</b> of Applicants per year</span>
                                                    {plansTab === 'Current' && <h4 onClick={addMoreApplicant}>Add More</h4>}
                                                </div>
                                            </div>
                                            <div
                                                data-is-show={isDetailShow}
                                                className={styles.plan_detail_action}
                                                onClick={() => plansDetailManager(indx)}
                                            >
                                                <span>{isDetailShow ? 'Hide' : 'See'} details</span>
                                                <ChevronDownIcon width={16} height={16} />
                                            </div>
                                        </div>
                                        {isDetailShow && <div className={styles.plan_table}>
                                            {item?.jobList?.length > 0 ? <>
                                                <table>
                                                    <thead>
                                                        <tr>
                                                            <th>Order number</th>
                                                            <th>Job title</th>
                                                            <th>Salary amount</th>
                                                            <th>Job post date</th>
                                                            <th>Job deadline</th>
                                                            <th>Applicants</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {item.jobList.map((list, indx) => <tr>
                                                            <td>{indx + 1}</td>
                                                            <td>{list.title}</td>
                                                            <td>{thousandSeparator(list.baseSalaryAmount || 0)}$ /{list.baseSalaryFrequency}</td>
                                                            <td>{list.lastModified?.slice(0, 10)}</td>
                                                            <td>{list.multipleChoiceExamDeadline?.slice(0, 10)}</td>
                                                            <td>{list.applicantCount}</td>
                                                        </tr>)}
                                                    </tbody>
                                                </table>

                                                <div className={styles.plan_table_cards}>
                                                    {item.jobList.map((list, indx) => <div className={styles.plan_table_cards_item}>
                                                        <div className={styles.plan_table_cards_item_header}>
                                                            <span>Job {indx + 1}:</span>
                                                            <b>{list.title}</b>
                                                        </div>
                                                        <div className={styles.plan_table_cards_item_content}>
                                                            <div className={styles.plan_table_cards_item_content_row}>
                                                                <span>Salary amount</span>
                                                                <b>{thousandSeparator(list.baseSalaryAmount || 0)}$ /{list.baseSalaryFrequency}</b>
                                                            </div>
                                                            <div className={styles.plan_table_cards_item_content_row}>
                                                                <span>Job post date</span>
                                                                <b>{list.lastModified?.slice(0, 10)}</b>
                                                            </div>
                                                            <div className={styles.plan_table_cards_item_content_row}>
                                                                <span>Job deadline</span>
                                                                <b>{list.multipleChoiceExamDeadline?.slice(0, 10)}</b>
                                                            </div>
                                                            <div className={styles.plan_table_cards_item_content_row}>
                                                                <span>Applicants</span>
                                                                <b>{list.applicantCount}</b>
                                                            </div>
                                                        </div>
                                                    </div>)}
                                                </div>
                                            </>
                                                : <div className={styles.plan_table_empty}>
                                                    <InformationIcon />
                                                    <span>No data.</span>
                                                </div>}
                                        </div>}
                                        {profile?.userRole === 'owner' && plansTab === 'Current' && <Button
                                            text='Change Plan'
                                            className={styles.plan_CTA}
                                            onClick={navigateToPayment}
                                        />}
                                    </div>)
                                })}
                            </> : <div className={styles.plan_info_list_empty}>
                                <InformationIcon />
                                <span>No data.</span>
                            </div>}
                        </div>
                    </div>
                </>)
            }
        </MotionWrapper >
    );
};